import React, {useState} from 'react';
import useTableSearchParams from '../../../utils/useTableSearchParams';
import {
	Chip,
	FormControl,
	InputAdornment,
	InputLabel,
	MenuItem,
	OutlinedInput,
	Paper,
	Select,
	Tab,
	Table,
	TableBody,
	TableContainer,
	TablePagination,
	Tabs,
	TextField,
} from "@mui/material";
import "./UsersTable.css"
import TableHeader from "../layout/table/TableHeader";
import {SearchOutlined} from "@mui/icons-material";
import Constants from "../../../utils/constants";
import {useNavigate, useLocation} from "react-router-dom";
import ModalDelete from "../layout/ModalDelete";
import Grid from "@mui/material/Grid2";
import ExploitantRow from "./rows/ExploitantRow";
import ClientRow from "./rows/ClientRow";
import OperateurRow from "./rows/OperateurRow";
import AgenceBadge from "../agences/AgenceBadge";

const headCellsExploitants = [
	{id: 'role', label: 'Role'},
	{id: 'libelle', label: 'Libellé'},
	{id: 'logo', label: 'Logo'},
	{id: 'email', label: 'Email'},
	{id: 'typologie', label: 'Informations'},
	{id: 'accredited', label: 'Accréditation', className: "th-small"},
	{id: 'actions', label: ''},
];

const headCellsClients = [
	{id: 'agence', label: 'Agence'},
	{id: 'libelle', label: 'Client'},
	{id: 'email', label: 'Email'},
	{id: 'typologie', label: 'Typologie'},
	{id: 'accredited', label: 'Accréditation', className: "th-small"},
	{id: 'actions', label: ''},
];

const headCellsOperateurs = [
	{id: 'role', label: 'Role'},
	{id: 'libelle', label: 'Libellé'},
	{id: 'logo', label: 'Logo'},
	{id: 'email', label: 'Email'},
	{id: 'laveuse', label: 'Laveuse'},
	{id: 'actions', label: ''},
];

function UsersTable({...props}) {
	const navigate = useNavigate();
	const location = useLocation();
	const [open, setOpen] = useState(false);
	const [currentUser, setCurrentUser] = useState(null);
	const [label, setLabel] = useState("");
	const [objectToDelete, setObjectToDelete] = useState(null);

	const {get: getParam, syncUrl} = useTableSearchParams({
		tab: 0,
		searchExp: '', searchCli: '', searchOpe: '',
		pageExp: 0, pageCli: 0, pageOpe: 0,
		rowsPerPage: 15,
		agences: [], typologies: [],
	});

	const activeTab        = getParam('tab');
	const searchExploitants = getParam('searchExp');
	const searchClients     = getParam('searchCli');
	const searchOperateurs  = getParam('searchOpe');
	const filterAgences     = getParam('agences');
	const filterTypologies  = getParam('typologies');
	const pageExploitants   = getParam('pageExp');
	const pageClients       = getParam('pageCli');
	const pageOperateurs    = getParam('pageOpe');
	const rowsPerPage       = getParam('rowsPerPage');

	const setFilterAgences    = (v) => syncUrl({agences: v, pageCli: 0});
	const setFilterTypologies = (v) => syncUrl({typologies: v, pageCli: 0});

	// Ordre (local uniquement, pas besoin de persister dans l'URL)
	const [orderExploitants, setOrderExploitants] = useState({order: 'asc', orderBy: 'libelle'});
	const [orderClients, setOrderClients] = useState({order: 'asc', orderBy: 'agence'});
	const [orderOperateurs, setOrderOperateurs] = useState({order: 'asc', orderBy: 'libelle'});

	// Flatten data by role
	const exploitants = props.users ?? [];
	const clients = (props.users ?? []).flatMap(u => u.clients ?? []);
	const operateurs = (props.users ?? []).flatMap(u => u.operateurs ?? []);

	const tabs = [
		{label: `Exploitants (${exploitants.length})`, data: exploitants, backgroundColor: 'var(--thm-black)', color: 'white'},
		{label: `Clients (${clients.length})`,         data: clients,     backgroundColor: 'var(--thm-orange)', color: 'white'},
		{label: `Opérateurs (${operateurs.length})`,   data: operateurs,  backgroundColor: 'var(--thm-base)', color: 'white'},
	];

	// Handlers
	const openDeleteConfirmModal = (u) => {
		setCurrentUser(u);
		setLabel("Etes vous sûr de vouloir supprimer l'utilisateur : ");
		setObjectToDelete(u.email);
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
		setCurrentUser(null);
	};

	const handleEditClick = (type, id) => {
		navigate(Constants.APP_ROUTE_ADMIN_USERS_EDIT.replace(":type", type).replace(':id', id), {state: {returnSearch: location.search}});
	};

	const handleDeleteUser = () => {
		props.onDelete(currentUser);
		handleClose();
	};

	const canDeleteUser = (user) => {
		return !user.roles?.includes("ROLE_ADMIN");
	};

	// Sort
	const getItemComparator = (item, comparator) => {
		if (comparator === "accredited") return item.isAccredited;
		if (comparator === "role") return item.roles;
		if (comparator === "agence") return item.agence?.libelle ?? null;
		return item[comparator];
	};

	const sortData = (data, order, orderBy) => {
		return [...data].sort((a, b) => {
			const valA = getItemComparator(a, orderBy);
			const valB = getItemComparator(b, orderBy);
			if (valA === null && valB === null) return 0;
			if (valA === null) return 1;
			if (valB === null) return -1;
			if (order === 'asc') return valA > valB ? 1 : valA < valB ? -1 : 0;
			return valA < valB ? 1 : valA > valB ? -1 : 0;
		});
	};

	const handleRequestSort = (setter) => (event, property) => {
		setter(prev => ({
			order: prev.orderBy === property && prev.order === 'asc' ? 'desc' : 'asc',
			orderBy: property,
		}));
	};

	// Filter
	const filterData = (data, search) => {
		const value = search.toLowerCase();
		console.log("filter : ", value)
		if (!value) return data;
		return data.filter(u =>
		  u.libelle?.toLowerCase().includes(value) ||
		  u.email?.toLowerCase().includes(value) ||
		  u.roles?.some(r => r.toLowerCase().includes(value))
		);
	};

	// Options agences depuis redux (+ option "Non rattaché")
	const agenceOptions = props.agences ?? [];
	const NON_RATTACHE_ID = '__non_rattache__';

	// Groupes de typologies abstraites
	const typologieGroups = Constants.TYPOLOGIE_CONTENEUR_GROUPS;

	// Per-tab computed data
	const filteredExploitants = filterData(sortData(exploitants, orderExploitants.order, orderExploitants.orderBy), searchExploitants);
	const filteredClients = (() => {
		let data = sortData(clients, orderClients.order, orderClients.orderBy);
		if (filterAgences.length > 0) {
			data = data.filter(c => {
				if (filterAgences.includes(NON_RATTACHE_ID) && !c.agence) return true;
				if (!c.agence) return false;
 			// c.agence est un objet JSON-LD avec "@id": "/api/agences/2"
				const iri = c.agence['@id'] ?? c.agence.id ?? c.agence;
				const agenceId = typeof iri === 'string' ? iri.split('/').pop() : String(iri);
				return filterAgences.includes(agenceId);
			});
		}
		if (filterTypologies.length > 0) {
			const selectedCodes = filterTypologies.flatMap(label =>
				typologieGroups.find(g => g.label === label)?.codes ?? []
			);
			data = data.filter(c => c.typologieConteneurs?.some(t => selectedCodes.includes(t)));
		}
		return filterData(data, searchClients);
	})();
	const filteredOperateurs  = filterData(sortData(operateurs,  orderOperateurs.order,  orderOperateurs.orderBy),  searchOperateurs);

	const tabConfig = [
		{
			headCells: headCellsExploitants,
			data: filteredExploitants,
			search: searchExploitants, setSearch: (v) => syncUrl({searchExp: v, pageExp: 0}),
			orderState: orderExploitants, setOrder: setOrderExploitants,
			page: pageExploitants, setPage: (v) => syncUrl({pageExp: v}),
			renderRow: (item) => (
			  <ExploitantRow key={item.id} exploitant={item}
			                 onEdit={handleEditClick} onDelete={openDeleteConfirmModal} canDelete={canDeleteUser}/>
			),
		},
		{
			headCells: headCellsClients,
			data: filteredClients,
			search: searchClients, setSearch: (v) => syncUrl({searchCli: v, pageCli: 0}),
			orderState: orderClients, setOrder: setOrderClients,
			page: pageClients, setPage: (v) => syncUrl({pageCli: v}),
			renderRow: (item) => (
			  <ClientRow key={item.id} client={item}
			             onEdit={handleEditClick} onDelete={openDeleteConfirmModal} canDelete={canDeleteUser}/>
			),
			extraFilters: (
			  <>
				  <FormControl size="small" sx={{minWidth: 180}}>
					  <InputLabel>Agence</InputLabel>
					  <Select
						multiple
						value={filterAgences}
 					onChange={(e) => { setFilterAgences(e.target.value); }}
						input={<OutlinedInput label="Agence"/>}
						renderValue={(selected) => (
						  <>{selected.map(id => {
							  if (id === NON_RATTACHE_ID) return <Chip key={id} label="Non rattaché" size="small" sx={{mr: 0.5}}/>;
 						  const a = agenceOptions.find(o => String(o.id) === id);
							  return <AgenceBadge key={id} agence={a} size="small"/>;
						  })}</>
						)}
					  >
 					  {agenceOptions.map(a => (
 						  <MenuItem key={a.id} value={String(a.id)}>
								  <AgenceBadge agence={a} size="small"/>
							  </MenuItem>
						  ))}
						  <MenuItem value={NON_RATTACHE_ID}>
							  <Chip label="Non rattaché" size="small"/>
						  </MenuItem>
					  </Select>
				  </FormControl>
				  <FormControl size="small" sx={{minWidth: 180}}>
					  <InputLabel>Typologie</InputLabel>
					  <Select
						multiple
						value={filterTypologies}
 					onChange={(e) => { setFilterTypologies(e.target.value); }}
						input={<OutlinedInput label="Typologie"/>}
						renderValue={(selected) => (
						  <>{selected.map(t => <Chip key={t} label={t} size="small" sx={{mr: 0.5}}/>)}</>
						)}
					  >
						  {typologieGroups.map(g => (
							  <MenuItem key={g.label} value={g.label}>{g.label}</MenuItem>
						  ))}
					  </Select>
				  </FormControl>
			  </>
			),
		},
		{
			headCells: headCellsOperateurs,
			data: filteredOperateurs,
			search: searchOperateurs, setSearch: (v) => syncUrl({searchOpe: v, pageOpe: 0}),
			orderState: orderOperateurs, setOrder: setOrderOperateurs,
			page: pageOperateurs, setPage: (v) => syncUrl({pageOpe: v}),
			renderRow: (item) => (
			  <OperateurRow key={item.id} operateur={item}
			                onEdit={handleEditClick} onDelete={openDeleteConfirmModal} canDelete={canDeleteUser}/>
			),
		},
	];

	const current = tabConfig[activeTab];

	const handleChangeRowsPerPage = (event) => {
		const val = parseInt(event.target.value, 10);
		syncUrl({rowsPerPage: val, pageExp: 0, pageCli: 0, pageOpe: 0});
	};

	return (
	  <React.Fragment>
		  <Grid container sx={{
			  alignItems: 'center',
			  justifyContent: 'space-between',
		  }}>
			  <Grid>
				  <Tabs
 				value={activeTab}
 				onChange={(e, val) => { syncUrl({tab: val, pageExp: 0, pageCli: 0, pageOpe: 0}); }}
					sx={{
						mb: 2,
						mt: 2,
						'& .MuiTabs-indicator': {
							backgroundColor: tabs[activeTab]?.backgroundColor,
						},
					}}
				  >
					  {tabs.map((tab, index) => (
						<Tab
						  key={index}
						  label={tab.label}
						  sx={{
							  fontWeight: 600,
							  color: 'text.secondary',
							  '&.Mui-selected': {
								  color: tab.color,
								  backgroundColor: tabs[activeTab]?.backgroundColor,
							  },
						  }}
						/>
					  ))}
				  </Tabs>
			  </Grid>
			  <Grid>
				  <TextField
					size="small"
					variant="outlined"
					placeholder="Rechercher..."
 				value={current.search}
 				onChange={(e) => { current.setSearch(e.target.value); }}
					sx={{
						ml: 1,
						mb: 2,
						'& .MuiOutlinedInput-root': {
							borderRadius: '24px',
							'&.Mui-focused fieldset': {
								borderColor: tabs[activeTab]?.backgroundColor,
							},
						},
						'& label.Mui-focused': {
							color: tabs[activeTab]?.backgroundColor,
						},
					}}
					slotProps={{
						input: {
							startAdornment: (
							  <InputAdornment position="start">
								  <SearchOutlined sx={{ color: tabs[activeTab]?.backgroundColor }}/>
							  </InputAdornment>
							),
						},
					}}
				  />
			  </Grid>
		  </Grid>

  		{current.extraFilters && (
  		  <Grid container spacing={1} sx={{mb: 2, alignItems: 'center'}}>
  			  {current.extraFilters}
  		  </Grid>
  		)}

  		<TableContainer component={Paper} className={"mt-2"}>
			  <Table aria-label="users tables" size="small">
  		  <TableHeader headCells={current.headCells}
				               order={current.orderState.order}
				               orderBy={current.orderState.orderBy}
				               onRequestSort={handleRequestSort(current.setOrder)}/>
				  <TableBody>
					  {current.data
						.slice(current.page * rowsPerPage, current.page * rowsPerPage + rowsPerPage)
						.map((item) => current.renderRow(item))
					  }
				  </TableBody>
			  </Table>
		  </TableContainer>

		  <TablePagination
			rowsPerPageOptions={[15, 25, 50]}
			component="div"
			count={current.data.length}
			rowsPerPage={rowsPerPage}
			labelRowsPerPage={"Nombre de lignes :"}
			labelDisplayedRows={({from, to, count}) =>
			  `${from} - ${to} / ${count !== -1 ? count : `Plus de ${to}`}`
			}
			page={current.page}
			onPageChange={(e, newPage) => current.setPage(newPage)}
			onRowsPerPageChange={handleChangeRowsPerPage}
		  />

		  <ModalDelete label={label} onClose={handleClose} open={open} objectToDelete={objectToDelete}
		               selectedValue={currentUser} onDelete={handleDeleteUser}/>
	  </React.Fragment>
	);
}

export default UsersTable;
